import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../AuthContext";
import {
	AI_CODE_REVIEWER_USERNAME,
	GITLAB_BASE_URL,
	GITLAB_GRAPHQL_URL,
	MURLOC_REVIEWER_USERNAME,
} from "../constants";

// Module-level caches so a card that is collapsed and re-expanded (or a board
// refresh that re-mounts cards) does not refetch the same notes repeatedly.
// Capped to avoid unbounded growth over long sessions (oldest entries evicted).
const notesCache = new Map();
const inFlight = new Map();
const NOTES_STORAGE_KEY = "gitlab-mr-notes-cache-v7";
const MAX_CACHE_ENTRIES = 200;
const FETCH_TIMEOUT_MS = 15000;
const NOTES_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
let hasHydratedPersistentCache = false;

const normalizeAwardEmojis = (awardEmojis = [], currentUsername = "") => {
	const grouped = new Map();

	for (const award of awardEmojis || []) {
		const name = award?.name;
		if (!name) continue;

		const existing = grouped.get(name) || {
			name,
			count: 0,
			currentUserReacted: false,
			currentUserAwardId: null,
		};
		existing.count += Number(award?.count) || 1;
		const currentUserReacted =
			Boolean(award?.currentUserReacted) ||
			(Boolean(currentUsername) && award?.user?.username === currentUsername);
		existing.currentUserReacted =
			existing.currentUserReacted || currentUserReacted;
		if (currentUserReacted && award?.id) {
			existing.currentUserAwardId = award.id;
		}
		grouped.set(name, existing);
	}

	return [...grouped.values()].sort((a, b) => a.name.localeCompare(b.name));
};

const getLocalStorage = () => {
	if (typeof window === "undefined") return null;
	return window.localStorage;
};

const persistNotesCache = () => {
	const storage = getLocalStorage();
	if (!storage) return;

	try {
		storage.setItem(
			NOTES_STORAGE_KEY,
			JSON.stringify({
				entries: Array.from(notesCache.entries()),
			}),
		);
	} catch (error) {
		console.error("Error writing MR notes cache:", error);
	}
};

const hydratePersistentNotesCache = () => {
	if (hasHydratedPersistentCache) return;
	hasHydratedPersistentCache = true;

	const storage = getLocalStorage();
	if (!storage) return;

	try {
		const raw = storage.getItem(NOTES_STORAGE_KEY);
		if (!raw) return;

		const parsed = JSON.parse(raw);
		const entries = Array.isArray(parsed?.entries) ? parsed.entries : [];
		const now = Date.now();

		for (const [mrId, selected] of entries) {
			if (!mrId || !selected) continue;
			const cachedAt = selected.cachedAt || 0;
			if (now - cachedAt > NOTES_CACHE_TTL_MS) continue;
			notesCache.set(mrId, selected);
		}
	} catch (error) {
		console.error("Error reading MR notes cache:", error);
	}
};

const cacheNotes = (mrId, selected) => {
	notesCache.set(mrId, { ...selected, cachedAt: Date.now() });
	while (notesCache.size > MAX_CACHE_ENTRIES) {
		const oldestKey = notesCache.keys().next().value;
		notesCache.delete(oldestKey);
	}
	persistNotesCache();
};

const buildNotesQuery = (id, after = null) => `
	query getMRNotes {
		mergeRequest(id: ${JSON.stringify(id)}) {
			notes(first: 100${after ? `, after: ${JSON.stringify(after)}` : ""}) {
				pageInfo {
					endCursor
					hasNextPage
				}
				nodes {
					id
					body
					bodyHtml
					system
					createdAt
					url
					author {
						username
						name
						avatarUrl
						bot
					}
				}
			}
		}
	}
`;

const fetchGraphqlNoteNodes = async (mrId, token, signal) => {
	const nodes = [];
	let after = null;
	let hasNextPage = true;
	let pageCount = 0;

	while (hasNextPage && pageCount < 10) {
		const response = await fetch(GITLAB_GRAPHQL_URL, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify({ query: buildNotesQuery(mrId, after) }),
			signal,
		});

		if (!response.ok) throw new Error("Failed to load comments");
		const result = await response.json();
		if (result.errors && !result.data) {
			throw new Error(result.errors[0]?.message || "Failed to load comments");
		}

		const notes = result.data?.mergeRequest?.notes;
		nodes.push(...(notes?.nodes || []));
		after = notes?.pageInfo?.endCursor || null;
		hasNextPage = Boolean(notes?.pageInfo?.hasNextPage && after);
		pageCount += 1;
	}

	return nodes;
};

const extractRestNoteId = (note) => {
	const candidates = [note?.restId, note?.id, note?.url].filter(Boolean);

	for (const candidate of candidates) {
		const value = String(candidate);
		const exactNumeric = value.match(/^\d+$/);
		if (exactNumeric) return exactNumeric[0];

		const noteAnchor = value.match(/note_(\d+)/);
		if (noteAnchor) return noteAnchor[1];

		const notePath = value.match(/\/notes\/(\d+)/);
		if (notePath) return notePath[1];

		const gitlabGid = value.match(/\/Note\/(\d+)$/);
		if (gitlabGid) return gitlabGid[1];
	}

	return "";
};

const parseGitLabError = async (response, fallback) => {
	try {
		const result = await response.json();
		if (
			result?.error === "insufficient_scope" ||
			result?.message === "insufficient_scope"
		) {
			const writeAction = /post|delete|remove/.test(fallback.toLowerCase());
			return writeAction
				? "Changing comments requires GitLab API write access. Sign out and sign back in to grant the updated scope."
				: "GitLab rejected the request because the current token is missing a required scope. Sign out and sign back in to refresh permissions.";
		}

		const message = result?.message || result?.error || result?.errors;

		if (typeof message === "string") return message;
		if (Array.isArray(message)) return message.join(", ");
		if (message && typeof message === "object") {
			return Object.entries(message)
				.map(([key, value]) => {
					const details = Array.isArray(value) ? value.join(", ") : value;
					return `${key} ${details}`;
				})
				.join(", ");
		}
	} catch {
		// Fall back to the caller's message.
	}

	return fallback;
};

const fetchAwardEmojisForNote = async ({
	projectPath,
	mrIid,
	note,
	token,
	currentUsername,
	signal,
}) => {
	const noteRestId = extractRestNoteId(note);
	if (!projectPath || !mrIid || !noteRestId || !token) return [];

	try {
		const response = await fetch(
			`${GITLAB_BASE_URL}/api/v4/projects/${encodeURIComponent(projectPath)}/merge_requests/${mrIid}/notes/${encodeURIComponent(noteRestId)}/award_emoji`,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
				signal,
			},
		);

		if (!response.ok) {
			throw new Error(
				await parseGitLabError(response, "Failed to load emoji reactions"),
			);
		}

		return normalizeAwardEmojis(await response.json(), currentUsername);
	} catch (error) {
		if (error.name === "AbortError") throw error;
		console.warn("Error loading emoji reactions:", error);
		return [];
	}
};

const enrichSelectedWithAwardEmojis = async ({
	selected,
	projectPath,
	mrIid,
	token,
	currentUsername,
	signal,
}) => {
	const notes = [
		...(selected?.allNotes || []),
		selected?.aiNote,
		selected?.murlocNote,
		selected?.atlantisNote,
		selected?.humanNote,
		...(selected?.discussions || []).flatMap(
			(discussion) => discussion.notes || [],
		),
	].filter(Boolean);
	const uniqueNotes = new Map();

	for (const note of notes) {
		const noteRestId = extractRestNoteId(note);
		if (noteRestId && !uniqueNotes.has(noteRestId)) {
			uniqueNotes.set(noteRestId, note);
		}
	}

	if (uniqueNotes.size === 0) return selected;

	const awardsByNoteId = new Map(
		await Promise.all(
			[...uniqueNotes.entries()].map(async ([noteRestId, note]) => [
				noteRestId,
				await fetchAwardEmojisForNote({
					projectPath,
					mrIid,
					note,
					token,
					currentUsername,
					signal,
				}),
			]),
		),
	);
	const addAwards = (note) => {
		if (!note) return note;
		const noteRestId = extractRestNoteId(note);
		return {
			...note,
			awardEmojis: awardsByNoteId.get(noteRestId) || note.awardEmojis || [],
		};
	};

	return {
		...selected,
		allNotes: (selected.allNotes || []).map(addAwards),
		aiNote: addAwards(selected.aiNote),
		murlocNote: addAwards(selected.murlocNote),
		atlantisNote: addAwards(selected.atlantisNote),
		humanNote: addAwards(selected.humanNote),
		discussions: (selected.discussions || []).map((discussion) => ({
			...discussion,
			notes: (discussion.notes || []).map(addAwards),
		})),
	};
};

const withAvatarBase = (url) => {
	if (!url) return null;
	return url.startsWith("https://") ? url : `${GITLAB_BASE_URL}${url}`;
};

// GitLab returns bodyHtml with root-relative links/images (e.g. href="/group/..",
// src="/uploads/..") that would otherwise resolve against the dashboard origin.
// Rewrite them to absolute GitLab URLs so they work from the dashboard.
const rewriteRelativeUrls = (html) => {
	if (!html) return "";
	return html
		.replace(/(href|src)="\/(?!\/)/g, `$1="${GITLAB_BASE_URL}/`)
		.replace(/(href|src)='\/(?!\/)/g, `$1='${GITLAB_BASE_URL}/`);
};

const isAi = (author) => author?.username === AI_CODE_REVIEWER_USERNAME;

const isMurloc = (author) => author?.username === MURLOC_REVIEWER_USERNAME;

// Atlantis is a Terraform policy/CI bot, not a human reviewer. GitLab does not
// always flag its account with `bot: true`, so match on identity as well.
const isAtlantis = (author) => {
	const username = author?.username?.toLowerCase() || "";
	const name = author?.name?.toLowerCase() || "";
	return username.includes("atlantis") || name === "atlantis";
};

const getNoteUrl = (note, restId, mrWebUrl) =>
	note?.url ||
	note?.web_url ||
	(restId && mrWebUrl ? `${mrWebUrl}#note_${restId}` : "");

const normalizeNote = (note, mrWebUrl = "", currentUsername = "") => {
	const restId = extractRestNoteId(note);

	return {
		id: note.id,
		restId,
		body: note.body || "",
		bodyHtml: rewriteRelativeUrls(note.bodyHtml || note.body_html || ""),
		createdAt: note.createdAt || note.created_at,
		url: getNoteUrl(note, restId, mrWebUrl),
		awardEmojis: normalizeAwardEmojis(
			note.award_emoji || note.awardEmoji || note.awardEmojis || [],
			currentUsername,
		),
		author: {
			username: note.author?.username,
			name: note.author?.name || note.author?.username || "Unknown",
			avatarUrl: withAvatarBase(
				note.author?.avatarUrl || note.author?.avatar_url,
			),
			bot: note.author?.bot ?? false,
		},
	};
};

const byCreatedAtAsc = (a, b) =>
	new Date(a.createdAt || a.created_at) - new Date(b.createdAt || b.created_at);

const normalizeDiscussion = (
	discussion,
	mrWebUrl = "",
	currentUsername = "",
) => {
	const notes = (discussion?.notes || [])
		.filter((note) => note && !note.system && note.author)
		.sort(byCreatedAtAsc)
		.map((note) => ({
			...normalizeNote(note, mrWebUrl, currentUsername),
			discussionId: discussion.id,
		}));

	return {
		id: discussion.id,
		individualNote: discussion.individual_note,
		notes,
	};
};

const selectGraphqlNotes = async (
	mrId,
	token,
	signal,
	mrWebUrl = "",
	currentUsername = "",
) => {
	const nodes = await fetchGraphqlNoteNodes(mrId, token, signal);
	return {
		...selectNotes(nodes, mrWebUrl, currentUsername),
		discussions: [],
	};
};

const selectNotes = (nodes, mrWebUrl = "", currentUsername = "") => {
	const usable = (nodes || [])
		.filter((n) => n && !n.system && n.author)
		.sort(byCreatedAtAsc);
	const allNotes = usable.map((note) =>
		normalizeNote(note, mrWebUrl, currentUsername),
	);

	let aiNote = null;
	let murlocNote = null;
	let atlantisNote = null;
	let humanNote = null;

	for (const note of usable) {
		if (isAi(note.author)) {
			aiNote = note;
		} else if (isMurloc(note.author)) {
			murlocNote = note;
		} else if (isAtlantis(note.author)) {
			atlantisNote = note;
		} else if (!note.author.bot) {
			humanNote = note;
		}
	}

	return {
		allNotes,
		aiNote: aiNote ? normalizeNote(aiNote, mrWebUrl, currentUsername) : null,
		murlocNote: murlocNote
			? normalizeNote(murlocNote, mrWebUrl, currentUsername)
			: null,
		atlantisNote: atlantisNote
			? normalizeNote(atlantisNote, mrWebUrl, currentUsername)
			: null,
		humanNote: humanNote
			? normalizeNote(humanNote, mrWebUrl, currentUsername)
			: null,
	};
};

/**
 * Lazily fetches the latest AI code-reviewer note and the latest human note for
 * a single merge request. Nothing is fetched until `load()` is called (i.e. on
 * card unfurl), so the board's initial render stays cheap.
 */
export const useMRNotes = (mrOrId) => {
	const { user } = useAuth();
	const mrId = typeof mrOrId === "object" ? mrOrId?.id : mrOrId;
	const projectPath =
		typeof mrOrId === "object"
			? mrOrId?.project?.fullPath || mrOrId?.projectPath
			: "";
	const mrIid = typeof mrOrId === "object" ? mrOrId?.iid : "";
	const mrWebUrl = typeof mrOrId === "object" ? mrOrId?.webUrl : "";
	const currentUsername =
		user?.profile?.username ||
		user?.profile?.preferred_username ||
		user?.profile?.nickname ||
		"";
	const [state, setState] = useState(() => {
		hydratePersistentNotesCache();
		const cached = notesCache.get(mrId);
		return {
			loading: false,
			error: null,
			loaded: !!cached,
			discussions: cached?.discussions ?? [],
			allNotes: cached?.allNotes ?? [],
			aiNote: cached?.aiNote ?? null,
			murlocNote: cached?.murlocNote ?? null,
			atlantisNote: cached?.atlantisNote ?? null,
			humanNote: cached?.humanNote ?? null,
		};
	});
	const [replying, setReplying] = useState(false);
	const [replyError, setReplyError] = useState(null);
	const [deletingNoteId, setDeletingNoteId] = useState(null);
	const [deleteError, setDeleteError] = useState(null);
	const [reactingEmojiKey, setReactingEmojiKey] = useState(null);
	const [reactionError, setReactionError] = useState(null);
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const load = useCallback(
		async ({ force = false } = {}) => {
			if (!mrId) return;

			if (force) {
				notesCache.delete(mrId);
				inFlight.delete(mrId);
				persistNotesCache();
			}

			const cached = notesCache.get(mrId);
			if (cached) {
				setState({
					loading: false,
					error: null,
					loaded: true,
					discussions: cached.discussions ?? [],
					allNotes: cached.allNotes ?? [],
					aiNote: cached.aiNote,
					murlocNote: cached.murlocNote,
					atlantisNote: cached.atlantisNote,
					humanNote: cached.humanNote,
				});
				return;
			}

			const token = user?.access_token;
			if (!token) {
				setState((prev) => ({ ...prev, error: "Not authenticated" }));
				return;
			}

			setState((prev) => ({ ...prev, loading: true, error: null }));

			try {
				let promise = inFlight.get(mrId);
				if (!promise) {
					promise = (async () => {
						// Abort the request if GitLab hangs so the card never spins forever.
						const controller = new AbortController();
						const timeoutId = setTimeout(
							() => controller.abort(),
							FETCH_TIMEOUT_MS,
						);
						try {
							if (projectPath && mrIid) {
								try {
									const rawDiscussions = [];
									let page = 1;
									let hasNextPage = true;

									while (hasNextPage && page <= 10) {
										const response = await fetch(
											`${GITLAB_BASE_URL}/api/v4/projects/${encodeURIComponent(projectPath)}/merge_requests/${mrIid}/discussions?per_page=100&page=${page}`,
											{
												headers: {
													Authorization: `Bearer ${token}`,
												},
												signal: controller.signal,
											},
										);

										if (!response.ok) {
											throw new Error(
												await parseGitLabError(
													response,
													"Failed to load threaded comments",
												),
											);
										}

										const pageDiscussions = await response.json();
										rawDiscussions.push(
											...(Array.isArray(pageDiscussions)
												? pageDiscussions
												: []),
										);

										const nextPage = response.headers.get("X-Next-Page");
										hasNextPage = Boolean(nextPage);
										page = nextPage ? Number(nextPage) : page + 1;
									}

									const visibleDiscussions = rawDiscussions
										.map((discussion) =>
											normalizeDiscussion(
												discussion,
												mrWebUrl,
												currentUsername,
											),
										)
										.filter((discussion) => discussion.notes.length > 0);
									if (visibleDiscussions.length > 0) {
										const selected = await enrichSelectedWithAwardEmojis({
											selected: {
												...selectNotes(
													visibleDiscussions.flatMap(
														(discussion) => discussion.notes,
													),
													mrWebUrl,
													currentUsername,
												),
												discussions: visibleDiscussions,
											},
											projectPath,
											mrIid,
											token,
											currentUsername,
											signal: controller.signal,
										});
										cacheNotes(mrId, selected);
										return selected;
									}
								} catch (error) {
									if (error.name === "AbortError") throw error;
									console.warn(
										"Error loading threaded comments; falling back to notes:",
										error,
									);
								}
							}

							const selected = await enrichSelectedWithAwardEmojis({
								selected: await selectGraphqlNotes(
									mrId,
									token,
									controller.signal,
									mrWebUrl,
									currentUsername,
								),
								projectPath,
								mrIid,
								token,
								currentUsername,
								signal: controller.signal,
							});
							cacheNotes(mrId, selected);
							return selected;
						} catch (err) {
							if (err.name === "AbortError") {
								throw new Error("Timed out loading comments");
							}
							throw err;
						} finally {
							clearTimeout(timeoutId);
						}
					})().finally(() => {
						inFlight.delete(mrId);
					});
					inFlight.set(mrId, promise);
				}

				const selected = await promise;
				if (!mountedRef.current) return;
				setState({
					loading: false,
					error: null,
					loaded: true,
					discussions: selected.discussions ?? [],
					allNotes: selected.allNotes ?? [],
					aiNote: selected.aiNote,
					murlocNote: selected.murlocNote,
					atlantisNote: selected.atlantisNote,
					humanNote: selected.humanNote,
				});
			} catch (err) {
				if (!mountedRef.current) return;
				setState((prev) => ({
					...prev,
					loading: false,
					error: err.message || "Failed to load comments",
				}));
			}
		},
		[currentUsername, mrId, mrIid, mrWebUrl, projectPath, user?.access_token],
	);

	const reply = useCallback(
		async (body, discussionId = null) => {
			const text = body.trim();
			if (!text) return;

			const token = user?.access_token;
			if (!token) {
				setReplyError("Not authenticated");
				throw new Error("Not authenticated");
			}
			if (!projectPath || !mrIid) {
				const message = "Cannot reply: missing merge request project data";
				setReplyError(message);
				throw new Error(message);
			}

			setReplying(true);
			setReplyError(null);
			const controller = new AbortController();
			const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

			try {
				const endpoint = discussionId
					? `${GITLAB_BASE_URL}/api/v4/projects/${encodeURIComponent(projectPath)}/merge_requests/${mrIid}/discussions/${encodeURIComponent(discussionId)}/notes`
					: `${GITLAB_BASE_URL}/api/v4/projects/${encodeURIComponent(projectPath)}/merge_requests/${mrIid}/notes`;
				const response = await fetch(endpoint, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({ body: text }),
					signal: controller.signal,
				});

				if (!response.ok) {
					throw new Error(
						await parseGitLabError(response, "Failed to post comment"),
					);
				}

				await load({ force: true });
			} catch (err) {
				const message =
					err.name === "AbortError"
						? "Timed out posting comment"
						: err.message || "Failed to post comment";
				setReplyError(message);
				throw new Error(message);
			} finally {
				clearTimeout(timeoutId);
				setReplying(false);
			}
		},
		[load, mrIid, projectPath, user?.access_token],
	);

	const canDeleteNote = useCallback(
		(note) =>
			Boolean(
				projectPath &&
					mrIid &&
					currentUsername &&
					extractRestNoteId(note) &&
					note?.author?.username === currentUsername,
			),
		[currentUsername, mrIid, projectPath],
	);

	const deleteNote = useCallback(
		async (note) => {
			const noteRestId = extractRestNoteId(note);
			if (!noteRestId) {
				const message = "Cannot delete: missing comment id";
				setDeleteError(message);
				throw new Error(message);
			}

			const token = user?.access_token;
			if (!token) {
				setDeleteError("Not authenticated");
				throw new Error("Not authenticated");
			}
			if (!projectPath || !mrIid) {
				const message = "Cannot delete: missing merge request project data";
				setDeleteError(message);
				throw new Error(message);
			}

			setDeletingNoteId(noteRestId);
			setDeleteError(null);
			const controller = new AbortController();
			const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

			try {
				const response = await fetch(
					`${GITLAB_BASE_URL}/api/v4/projects/${encodeURIComponent(projectPath)}/merge_requests/${mrIid}/notes/${encodeURIComponent(noteRestId)}`,
					{
						method: "DELETE",
						headers: {
							Authorization: `Bearer ${token}`,
						},
						signal: controller.signal,
					},
				);

				if (!response.ok) {
					throw new Error(
						await parseGitLabError(response, "Failed to delete comment"),
					);
				}

				await load({ force: true });
			} catch (err) {
				const message =
					err.name === "AbortError"
						? "Timed out deleting comment"
						: err.message || "Failed to delete comment";
				setDeleteError(message);
				throw new Error(message);
			} finally {
				clearTimeout(timeoutId);
				setDeletingNoteId(null);
			}
		},
		[load, mrIid, projectPath, user?.access_token],
	);

	const addEmojiReaction = useCallback(
		async (note, name) => {
			const noteRestId = extractRestNoteId(note);
			if (!noteRestId) {
				const message = "Cannot react: missing comment id";
				setReactionError(message);
				throw new Error(message);
			}
			if (!name) {
				const message = "Cannot react: missing emoji";
				setReactionError(message);
				throw new Error(message);
			}

			const token = user?.access_token;
			if (!token) {
				setReactionError("Not authenticated");
				throw new Error("Not authenticated");
			}
			if (!projectPath || !mrIid) {
				const message = "Cannot react: missing merge request project data";
				setReactionError(message);
				throw new Error(message);
			}

			const reactionKey = `${noteRestId}:${name}`;
			setReactingEmojiKey(reactionKey);
			setReactionError(null);
			const controller = new AbortController();
			const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

			try {
				const response = await fetch(
					`${GITLAB_BASE_URL}/api/v4/projects/${encodeURIComponent(projectPath)}/merge_requests/${mrIid}/notes/${encodeURIComponent(noteRestId)}/award_emoji?name=${encodeURIComponent(name)}`,
					{
						method: "POST",
						headers: {
							Authorization: `Bearer ${token}`,
						},
						signal: controller.signal,
					},
				);

				if (!response.ok) {
					throw new Error(
						await parseGitLabError(response, "Failed to add emoji reaction"),
					);
				}

				await load({ force: true });
			} catch (err) {
				const message =
					err.name === "AbortError"
						? "Timed out adding emoji reaction"
						: err.message || "Failed to add emoji reaction";
				setReactionError(message);
				throw new Error(message);
			} finally {
				clearTimeout(timeoutId);
				setReactingEmojiKey(null);
			}
		},
		[load, mrIid, projectPath, user?.access_token],
	);

	const removeEmojiReaction = useCallback(
		async (note, name) => {
			const noteRestId = extractRestNoteId(note);
			if (!noteRestId) {
				const message = "Cannot remove reaction: missing comment id";
				setReactionError(message);
				throw new Error(message);
			}
			if (!name) {
				const message = "Cannot remove reaction: missing emoji";
				setReactionError(message);
				throw new Error(message);
			}

			const token = user?.access_token;
			if (!token) {
				setReactionError("Not authenticated");
				throw new Error("Not authenticated");
			}
			if (!projectPath || !mrIid) {
				const message =
					"Cannot remove reaction: missing merge request project data";
				setReactionError(message);
				throw new Error(message);
			}

			const reactionKey = `${noteRestId}:${name}`;
			setReactingEmojiKey(reactionKey);
			setReactionError(null);
			const controller = new AbortController();
			const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

			try {
				let awardId = (note.awardEmojis || []).find(
					(award) => award.name === name,
				)?.currentUserAwardId;

				if (!awardId) {
					const latestAwards = await fetchAwardEmojisForNote({
						projectPath,
						mrIid,
						note,
						token,
						currentUsername,
						signal: controller.signal,
					});
					awardId = latestAwards.find(
						(award) => award.name === name,
					)?.currentUserAwardId;
				}

				if (!awardId) {
					throw new Error("Cannot remove reaction: missing emoji id");
				}

				const response = await fetch(
					`${GITLAB_BASE_URL}/api/v4/projects/${encodeURIComponent(projectPath)}/merge_requests/${mrIid}/notes/${encodeURIComponent(noteRestId)}/award_emoji/${encodeURIComponent(awardId)}`,
					{
						method: "DELETE",
						headers: {
							Authorization: `Bearer ${token}`,
						},
						signal: controller.signal,
					},
				);

				if (!response.ok) {
					throw new Error(
						await parseGitLabError(response, "Failed to remove emoji reaction"),
					);
				}

				await load({ force: true });
			} catch (err) {
				const message =
					err.name === "AbortError"
						? "Timed out removing emoji reaction"
						: err.message || "Failed to remove emoji reaction";
				setReactionError(message);
				throw new Error(message);
			} finally {
				clearTimeout(timeoutId);
				setReactingEmojiKey(null);
			}
		},
		[currentUsername, load, mrIid, projectPath, user?.access_token],
	);

	return {
		...state,
		load,
		reply,
		replyToDiscussion: (body, discussionId) => reply(body, discussionId),
		replying,
		replyError,
		canReply: Boolean(projectPath && mrIid),
		deleteNote,
		canDeleteNote,
		deletingNoteId,
		deleteError,
		addEmojiReaction,
		removeEmojiReaction,
		canReact: Boolean(projectPath && mrIid),
		reactingEmojiKey,
		reactionError,
		mrWebUrl,
	};
};

// Allows a board refresh to drop stale note caches if ever needed.
export const clearMRNotesCache = () => {
	notesCache.clear();
	inFlight.clear();

	const storage = getLocalStorage();
	if (storage) {
		storage.removeItem(NOTES_STORAGE_KEY);
	}
};
